import React, { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { usePageMeta } from '../../utils/seo';
import AdminNav from './AdminNav';
import { field, fieldError, labelStyle, errorStyle, hintStyle } from '../../components/formStyles';
import { isValidKennitala, formatKennitala } from '../../utils/kennitala';

const kr = (n) => `${Number(n).toLocaleString('en-GB').replace(/,/g, '.')} kr.`;
const payUrl = (token) => `${window.location.origin}/pay/${token}`;
const STATUS = { open: ['Open', '#c89b3c'], paid: ['Paid', '#7bc07b'], cancelled: ['Cancelled', '#888'] };
const EMPTY = { payerName: '', kennitala: '', email: '', reason: '', amount: '' };

const call = async (session, body) => {
    const res = await fetch('/api/pay-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(data.error || `Request failed (${res.status})`); err.fields = data.errors; throw err; }
    return data;
};

const CopyButton = ({ text }) => {
    const [done, setDone] = useState(false);
    return (
        <button type="button" className="btn btn-outline" style={{ background: 'transparent', padding: '6px 14px', fontSize: '12px' }}
            onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 2000); } catch (e) { window.prompt('Copy the link', text); } }}>
            {done ? 'Copied' : 'Copy link'}
        </button>
    );
};

/** /admin/payments: make a payment link for anyone (name, kennitala, reason, amount) and follow it up. */
const AdminPayments = ({ session }) => {
    usePageMeta({ title: 'Payment links', path: '/admin/payments' });
    const [links, setLinks] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null);

    const load = async () => {
        const { data, error: err } = await supabase.from('payment_links').select('*').order('created_at', { ascending: false });
        if (err) setError(err.message); else setLinks(data);
    };
    useEffect(() => { load(); }, []);

    const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: undefined })); };

    const submit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!form.payerName.trim()) errs.payerName = 'Name is required';
        if (!isValidKennitala(form.kennitala)) errs.kennitala = 'Not a valid kennitala';
        if (!form.reason.trim()) errs.reason = 'Reason is required';
        const amount = Number(form.amount.replace(/[.\s]/g, ''));
        if (!Number.isInteger(amount) || amount < 100) errs.amount = 'Whole krónur, 100 or more';
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setBusy(true); setError(null);
        try {
            const r = await call(session, { action: 'create', ...form, amount });
            setCreated(r.link);
            setForm(EMPTY);
            load();
        } catch (err) {
            setError(err.message);
            if (err.fields) setErrors(err.fields);
        } finally { setBusy(false); }
    };

    const cancel = async (l) => {
        if (!window.confirm(`Cancel the link for ${l.payer_name} (${kr(l.amount_isk)})? It will stop working.`)) return;
        try { await call(session, { action: 'cancel', id: l.id }); load(); } catch (err) { setError(err.message); }
    };

    const Field = ({ k, label, hint, ...props }) => (
        <div>
            <label htmlFor={`pl-${k}`} style={labelStyle}>{label}</label>
            <input id={`pl-${k}`} value={form[k]} onChange={set(k)} style={errors[k] ? fieldError : field} aria-invalid={!!errors[k]} {...props} />
            {errors[k] ? <p style={errorStyle}>{errors[k]}</p> : hint ? <p style={hintStyle}>{hint}</p> : null}
        </div>
    );

    return (
        <div style={{ padding: '30px 20px 100px', maxWidth: '1100px', margin: '0 auto' }}>
            <AdminNav session={session} />
            <h1 style={{ fontFamily: 'var(--font-heading)', color: '#f0e6cc', fontSize: '36px', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 24px' }}>Payment Links</h1>

            <form onSubmit={submit} noValidate className="pl-form" style={{ border: '1px solid #333', background: '#111', padding: '24px', marginBottom: '32px' }}>
                {Field({ k: 'payerName', label: 'Payer name', autoComplete: 'off' })}
                {Field({ k: 'kennitala', label: 'Kennitala', inputMode: 'numeric', placeholder: '000000-0000', autoComplete: 'off' })}
                {Field({ k: 'amount', label: 'Amount (kr.)', inputMode: 'numeric', placeholder: '25000', autoComplete: 'off' })}
                {Field({ k: 'email', label: 'Payer email (optional)', type: 'email', hint: 'Teya sends the receipt here', autoComplete: 'off' })}
                <div style={{ gridColumn: '1 / -1' }}>
                    <label htmlFor="pl-reason" style={labelStyle}>Reason / invoice</label>
                    <input id="pl-reason" value={form.reason} onChange={set('reason')} maxLength={500} style={errors.reason ? fieldError : field} placeholder="e.g. Invoice 1042 – private event 12 October" />
                    {errors.reason ? <p style={errorStyle}>{errors.reason}</p> : <p style={hintStyle}>The payer sees this on the payment page and on the card receipt.</p>}
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create payment link'}</button>
                    {error && <p style={{ ...errorStyle, margin: 0 }}>{error}</p>}
                </div>
            </form>

            {created && (
                <div style={{ border: '1px solid #7bc07b', background: '#0f1a0f', padding: '18px 20px', marginBottom: '32px' }}>
                    <p style={{ color: '#7bc07b', margin: '0 0 8px', fontFamily: 'var(--font-heading)', letterSpacing: '2px', textTransform: 'uppercase' }}>Link ready: {created.payer_name}, {kr(created.amount_isk)}</p>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <code style={{ color: '#f0e6cc', wordBreak: 'break-all', fontSize: '14px' }}>{payUrl(created.token)}</code>
                        <CopyButton text={payUrl(created.token)} />
                    </div>
                </div>
            )}

            {links === null && !error && <p className="text-gold" style={{ letterSpacing: '3px' }}>LOADING…</p>}
            {links && links.length === 0 && <p style={{ color: '#666' }}>No payment links yet.</p>}
            <div style={{ display: 'grid', gap: '10px' }}>
                {(links || []).map((l) => (
                    <div key={l.id} className="pl-row" style={{ border: '1px solid #222', background: '#111', padding: '14px 16px' }}>
                        <div>
                            <span style={{ fontFamily: 'var(--font-heading)', color: '#f0e6cc', fontSize: '20px', letterSpacing: '1px' }}>{l.payer_name}</span>
                            <span style={{ color: '#888', marginLeft: '12px', fontSize: '13px' }}>kt. {formatKennitala(l.payer_kennitala)}</span>
                            <br />
                            <span style={{ color: '#ccc', fontSize: '14px' }}>{l.reason}</span>
                            <br />
                            <span style={{ color: '#777', fontSize: '12px' }}>
                                {l.ref} · created {new Date(l.created_at).toLocaleDateString('en-GB')}
                                {l.paid_at && ` · paid ${new Date(l.paid_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}`}
                                {l.card_last4 && ` · card ${l.card_last4}`}
                            </span>
                        </div>
                        <div style={{ textAlign: 'right', display: 'grid', gap: '8px', justifyItems: 'end' }}>
                            <span style={{ fontFamily: 'var(--font-heading)', color: '#f0e6cc', fontSize: '20px' }}>{kr(l.amount_isk)}</span>
                            <span style={{ color: STATUS[l.status][1], fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase' }}>{STATUS[l.status][0]}</span>
                            {l.status === 'open' && (
                                <span style={{ display: 'flex', gap: '8px' }}>
                                    <CopyButton text={payUrl(l.token)} />
                                    <button type="button" onClick={() => cancel(l)} style={{ background: 'none', border: 'none', color: '#b04a4a', cursor: 'pointer', font: 'inherit', fontSize: '12px' }}>Cancel</button>
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <style>{`
                .pl-form { display: grid; gap: 18px; }
                .pl-row { display: grid; gap: 12px; }
                @media (min-width: 720px) {
                    .pl-form { grid-template-columns: 1fr 1fr; }
                    .pl-row { grid-template-columns: 1fr auto; align-items: center; }
                }
            `}</style>
        </div>
    );
};

export default AdminPayments;
